import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_SITE } from '../siteDefaults';
import { fetchSite, getToken, saveSite } from './api';
import { input, label } from './adminUi';

export default function FooterDashboard() {
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!getToken()) {
      navigate('/admin', { replace: true });
      return;
    }
    fetchSite()
      .then(setSite)
      .catch(() => setSite(JSON.parse(JSON.stringify(DEFAULT_SITE))));
  }, [navigate]);

  function updateFooter(field, value) {
    setSite((s) => ({ ...s, footer: { ...s.footer, [field]: value } }));
  }

  function updateServiceLink(i, field, value) {
    setSite((s) => {
      const serviceLinks = [...s.footer.serviceLinks];
      serviceLinks[i] = { ...serviceLinks[i], [field]: value };
      return { ...s, footer: { ...s.footer, serviceLinks } };
    });
  }

  function addServiceLink() {
    setSite((s) => ({
      ...s,
      footer: {
        ...s.footer,
        serviceLinks: [...s.footer.serviceLinks, { label: '', href: '#' }],
      },
    }));
  }

  function removeServiceLink(i) {
    setSite((s) => ({
      ...s,
      footer: {
        ...s.footer,
        serviceLinks: s.footer.serviceLinks.filter((_, j) => j !== i),
      },
    }));
  }

  async function save() {
    setMsg('');
    setErr('');
    setSaving(true);
    try {
      const updated = await saveSite(site);
      setSite(updated);
      setMsg('Saved.');
    } catch (e) {
      setErr(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (!site) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-neutral-600">Loading…</div>
    );
  }

  return (
    <>
      <header className="z-10 flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-neutral-200 bg-white px-4 py-3 md:px-8">
        <div>
          <p className="text-xs font-medium text-neutral-500">Site content</p>
          <h2 className="text-lg font-bold text-neutral-900 md:text-xl">Footer</h2>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="rounded-lg bg-brand-forest px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save all'}
        </button>
      </header>

      <main className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-y-contain px-4 py-6 md:px-8 md:py-8">
        {msg ? <p className="mb-4 text-sm text-green-700">{msg}</p> : null}
        {err ? <p className="mb-4 text-sm text-red-600">{err}</p> : null}

        <section className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-lg font-bold text-neutral-900">Footer</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className={label}>
              Phone (display)
              <input
                className={input}
                value={site.footer.phone}
                onChange={(e) => updateFooter('phone', e.target.value)}
              />
            </label>
            <label className={label}>
              Phone (tel: href)
              <input
                className={input}
                value={site.footer.telHref}
                onChange={(e) => updateFooter('telHref', e.target.value)}
              />
            </label>
            <label className={label}>
              Email (display)
              <input
                className={input}
                value={site.footer.email}
                onChange={(e) => updateFooter('email', e.target.value)}
              />
            </label>
            <label className={label}>
              Email (mailto: href)
              <input
                className={input}
                value={site.footer.mailtoHref}
                onChange={(e) => updateFooter('mailtoHref', e.target.value)}
              />
            </label>
            <label className={`${label} sm:col-span-2`}>
              Blurb (newsletter column)
              <textarea
                className={`${input} min-h-[5rem]`}
                value={site.footer.blurb}
                onChange={(e) => updateFooter('blurb', e.target.value)}
              />
            </label>
            <label className={label}>
              Copyright name
              <input
                className={input}
                value={site.footer.copyrightName}
                onChange={(e) => updateFooter('copyrightName', e.target.value)}
              />
            </label>
            <label className={label}>
              Developer name
              <input
                className={input}
                value={site.footer.developerName}
                onChange={(e) => updateFooter('developerName', e.target.value)}
              />
            </label>
            <label className={`${label} sm:col-span-2`}>
              Developer URL
              <input
                className={input}
                value={site.footer.developerUrl}
                onChange={(e) => updateFooter('developerUrl', e.target.value)}
              />
            </label>
            <label className={label}>
              Facebook URL
              <input
                className={input}
                value={site.footer.social.facebook}
                onChange={(e) =>
                  setSite((s) => ({
                    ...s,
                    footer: {
                      ...s.footer,
                      social: { ...s.footer.social, facebook: e.target.value },
                    },
                  }))
                }
              />
            </label>
            <label className={label}>
              YouTube URL
              <input
                className={input}
                value={site.footer.social.youtube}
                onChange={(e) =>
                  setSite((s) => ({
                    ...s,
                    footer: {
                      ...s.footer,
                      social: { ...s.footer.social, youtube: e.target.value },
                    },
                  }))
                }
              />
            </label>
          </div>

          <h3 className="mb-3 mt-8 text-base font-semibold text-neutral-800">Service links</h3>
          <div className="space-y-3">
            {site.footer.serviceLinks.map((link, i) => (
              <div key={i} className="flex flex-wrap gap-2 sm:flex-nowrap">
                <input
                  className={input}
                  placeholder="Label"
                  value={link.label}
                  onChange={(e) => updateServiceLink(i, 'label', e.target.value)}
                />
                <input
                  className={input}
                  placeholder="Href"
                  value={link.href}
                  onChange={(e) => updateServiceLink(i, 'href', e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => removeServiceLink(i)}
                  className="shrink-0 rounded-lg px-3 text-sm text-red-600"
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addServiceLink}
              className="text-sm font-medium text-brand-olive hover:underline"
            >
              + Add service link
            </button>
          </div>
        </section>
      </main>
    </>
  );
}
